// =============================================================================
// features/documents/components/DocumentPreviewDrawer.tsx — Preview lateral
// de documento (F46-S08). Imagem inline; PDF em <iframe>. Sem viewer próprio.
//
// Por que blob e não a signed URL direta no `src`: `GET /api/documents/:id/download`
// exige `Authorization: Bearer` (rota autenticada, `documents:download`
// separada de `documents:read` — DPIA F46-S16). Um `<img>`/`<iframe>` não
// consegue enviar esse header. O drawer busca o blob autenticado
// (`downloadDocumentBlob`) e expõe como Object URL — nunca a URL assinada do
// storage diretamente. Sem `documents:download`: mensagem de permissão, nunca
// erro genérico (nota do slot: "o 403 do backend não pode virar erro feio").
// =============================================================================

import type { Document } from '@elemento/shared-schemas';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../../lib/cn';
import { downloadDocumentBlob, DocumentDownloadForbiddenError } from '../api';

import { resolveDocumentDisplayName } from './DocumentCard';

export interface DocumentPreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  /** Espelha `documentsGate.canDownload` — o preview usa a MESMA rota do download. */
  canDownload: boolean;
}

type PreviewState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; objectUrl: string; contentType: string };

function MessageBox({
  tone,
  title,
  detail,
}: {
  tone: 'warning' | 'danger';
  title: string;
  detail?: string;
}): React.JSX.Element {
  return (
    <div
      className="rounded-md p-8 flex flex-col items-center gap-2 text-center"
      style={{ background: `var(--${tone}-bg)`, border: `1px solid var(--${tone})` }}
    >
      <p
        className="font-sans font-semibold"
        style={{ fontSize: 'var(--text-sm)', color: `var(--${tone})` }}
      >
        {title}
      </p>
      {detail && (
        <p className="font-sans" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>
          {detail}
        </p>
      )}
    </div>
  );
}

export function DocumentPreviewDrawer({
  open,
  onClose,
  document,
  canDownload,
}: DocumentPreviewDrawerProps): React.JSX.Element | null {
  const [state, setState] = React.useState<PreviewState>({ phase: 'idle' });

  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.document.addEventListener('keydown', handleKey);
    return () => window.document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  React.useEffect(() => {
    if (!open || !document) {
      setState({ phase: 'idle' });
      return;
    }
    if (!canDownload) {
      setState({ phase: 'forbidden' });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ phase: 'loading' });

    void (async () => {
      try {
        const { blob, contentType } = await downloadDocumentBlob(document.id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ phase: 'ready', objectUrl, contentType });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DocumentDownloadForbiddenError) {
          setState({ phase: 'forbidden' });
        } else {
          setState({
            phase: 'error',
            message: err instanceof Error ? err.message : 'Não foi possível carregar o preview.',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, document?.id, canDownload]);

  if (!open || !document) return null;

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/20 backdrop-blur-[2px]"
        onClick={onClose}
        style={{ animation: 'fade-in 200ms ease both' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-preview-title"
        className={cn(
          'fixed right-0 top-0 bottom-0 z-[160] w-full sm:max-w-[640px] flex flex-col border-l border-border',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          animation: 'slide-in-right 300ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle shrink-0">
          <h2
            id="document-preview-title"
            className="font-display font-bold text-ink truncate"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            {resolveDocumentDisplayName(document) ?? 'Documento'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={cn(
              'w-8 h-8 shrink-0 flex items-center justify-center rounded-sm text-ink-3',
              'hover:text-ink hover:bg-surface-hover transition-all duration-fast ease',
              'focus-visible:ring-2 focus-visible:ring-azul/20 focus-visible:outline-none',
            )}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-5 h-5"
            >
              <path d="M5 5l10 10M15 5l-10 10" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {state.phase === 'loading' && (
            <div
              className="w-full h-full min-h-[300px] rounded-md animate-pulse"
              style={{ background: 'var(--surface-muted)' }}
            />
          )}

          {state.phase === 'forbidden' && (
            <MessageBox
              tone="warning"
              title="Você não tem permissão para visualizar este documento."
              detail="Visualizar exige a mesma permissão de download."
            />
          )}

          {state.phase === 'error' && <MessageBox tone="danger" title={state.message} />}

          {state.phase === 'ready' && state.contentType.startsWith('image/') && (
            <img
              src={state.objectUrl}
              alt={resolveDocumentDisplayName(document) ?? 'Documento'}
              className="max-w-full h-auto mx-auto rounded-md"
              style={{ boxShadow: 'var(--elev-2)' }}
            />
          )}

          {state.phase === 'ready' && !state.contentType.startsWith('image/') && (
            <iframe
              src={state.objectUrl}
              title={resolveDocumentDisplayName(document) ?? 'Documento'}
              className="w-full h-full min-h-[70vh] rounded-md border border-border"
            />
          )}
        </div>
      </div>
    </>,
    window.document.body,
  );
}
